import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { money, partCategoryLabel, titleCase, PART_STATUSES } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Eye, EyeOff, Trash2, Wrench, Search } from "lucide-react";

const STATUS_STYLE = {
  available: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  reserved: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  sold_out: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  hidden: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function AdminParts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/admin/parts", { params })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load parts"))
      .finally(() => setLoading(false));
  }, [q, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggleVisibility = async (p) => {
    const next = p.status === "hidden" ? "available" : "hidden";
    try {
      await api.patch(`/admin/parts/${p.id}/status`, { status: next });
      toast.success(next === "hidden" ? "Part hidden" : "Part visible");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/parts/${toDelete.id}`);
      toast.success("Part deleted");
      setItems((prev) => prev.filter((x) => x.id !== toDelete.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setToDelete(null); }
  };

  return (
    <div data-testid="admin-parts">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Wrench size={22} className="text-[#c9a86a]" /> Parts</h1>
          <p className="text-sm text-[var(--text-2)] mt-1">Manage the watch parts inventory shown on the public Parts page.</p>
        </div>
        <Button onClick={() => navigate("/admin/parts/new")} data-testid="add-part-btn" className="bg-[var(--panel-2)] hover:bg-[var(--border-2)] text-[#c9a86a]">
          <Plus size={16} className="mr-1.5" /> Add Part
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-2)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, brand, ref, part #" className="pl-9 bg-[var(--panel)]" data-testid="admin-parts-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-[var(--panel)]" data-testid="admin-parts-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {PART_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-[var(--border-lch)] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--panel-2)] text-[var(--text-2)] text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left p-3">Part</th>
              <th className="text-left p-3 hidden md:table-cell">Category</th>
              <th className="text-left p-3 hidden lg:table-cell">Brand / Ref</th>
              <th className="text-left p-3">Qty</th>
              <th className="text-left p-3">Price</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-[var(--muted)]">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-[var(--muted)]" data-testid="admin-parts-empty">No parts yet. Click “Add Part” to create one.</td></tr>
            ) : items.map((p) => {
              const photo = (p.public_photos && p.public_photos[0]) || null;
              return (
                <tr key={p.id} className="border-t border-[var(--border-lch)] hover:bg-[var(--panel)]/40" data-testid={`admin-part-row-${p.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded bg-[var(--panel)] overflow-hidden flex items-center justify-center shrink-0">
                        {photo ? <img src={fileUrl(photo.url || photo)} alt="" className="h-full w-full object-cover" /> : <Wrench size={16} className="text-[var(--border-2)]" />}
                      </div>
                      <span className="font-medium line-clamp-2 max-w-[280px]">{p.title}</span>
                    </div>
                  </td>
                  <td className="p-3 hidden md:table-cell text-[var(--text-2)]">{partCategoryLabel(p.part_category)}</td>
                  <td className="p-3 hidden lg:table-cell text-[var(--text-2)]">{[p.brand, p.reference_number].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="p-3">{p.quantity ?? 0}</td>
                  <td className="p-3">{p.price_on_request || p.price == null ? <span className="text-[var(--text-2)]">POR</span> : money(p.price)}</td>
                  <td className="p-3"><Badge variant="outline" className={STATUS_STYLE[p.status] || ""}>{titleCase(p.status)}</Badge></td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" title="Edit" onClick={() => navigate(`/admin/parts/${p.id}`)} data-testid={`edit-part-${p.id}`}><Pencil size={15} /></Button>
                      <Button size="icon" variant="ghost" title={p.status === "hidden" ? "Show" : "Hide"} onClick={() => toggleVisibility(p)} data-testid={`toggle-part-${p.id}`}>{p.status === "hidden" ? <Eye size={15} /> : <EyeOff size={15} />}</Button>
                      <Button size="icon" variant="ghost" title="Delete" onClick={() => setToDelete(p)} data-testid={`delete-part-${p.id}`} className="text-red-400 hover:text-red-500"><Trash2 size={15} /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this part?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.title}” will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-part">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
